import { useState, type ReactNode } from 'react';

import { toEnglishDigits, toPersianDigits } from '@bookora/shared';
import { Button, Icon, Modal, Numeric, Space, TrackingCode, Typography } from '@bookora/ui';
import type { ButtonProps } from '@bookora/ui';

import { lastApiError, type LastApiError } from '@/lib/feedback';

import { SUPPORT } from './support-config';

/** The last failure's tracking code is offered for this long after it was shown. */
const LAST_ERROR_TTL_MS = 30 * 60_000;

/**
 * The last failure worth telling support about: shown within the last half
 * hour and carrying a tracking code. Read when the dialog opens.
 */
function recentError(now = Date.now()): LastApiError | null {
  const last = lastApiError();
  return last && now - last.at < LAST_ERROR_TTL_MS ? last : null;
}

/**
 * The channels as a short list of real links. Used inline — under the 2FA
 * «به برنامه دسترسی ندارم», on a refused sign-in — and inside the dialog.
 */
export function SupportContactList({ compact = false }: { compact?: boolean }) {
  const rows: { key: string; icon: ReactNode; node: ReactNode }[] = [];

  if (SUPPORT.phone) {
    const dialable = toEnglishDigits(SUPPORT.phone).replace(/[^\d+]/g, '');
    rows.push({
      key: 'phone',
      icon: <Icon name="call" size={16} />,
      node: (
        <a href={`tel:${dialable}`}>
          تلفن پشتیبانی: <Numeric>{toPersianDigits(SUPPORT.phone)}</Numeric>
        </a>
      ),
    });
  }

  if (SUPPORT.telegram) {
    rows.push({
      key: 'telegram',
      icon: <Icon name="chat" size={16} />,
      node: (
        <a href={`https://t.me/${SUPPORT.telegram}`} target="_blank" rel="noreferrer">
          پیام در تلگرام: <Numeric>@{SUPPORT.telegram}</Numeric>
        </a>
      ),
    });
  }

  rows.push({
    key: 'form',
    icon: <Icon name="website" size={16} />,
    node: (
      <a href={SUPPORT.contactPage} target="_blank" rel="noreferrer">
        فرم تماس با پشتیبانی در سایت بوکورا
      </a>
    ),
  });

  rows.push({
    key: 'email',
    icon: <Icon name="send" size={16} />,
    node: (
      <a href={`mailto:${SUPPORT.email}`}>
        ایمیل: <Numeric>{SUPPORT.email}</Numeric>
      </a>
    ),
  });

  return (
    <Space orientation="vertical" size={compact ? 4 : 8} style={{ width: '100%' }}>
      <ul className="bk-support-list" aria-label="راه‌های تماس با پشتیبانی بوکورا">
        {rows.map((row) => (
          <li key={row.key}>
            <span aria-hidden="true" className="bk-support-list__icon">
              {row.icon}
            </span>
            {row.node}
          </li>
        ))}
      </ul>
      <Typography.Text className="bk-subtle" style={{ fontSize: 'var(--bk-text-caption)' }}>
        ساعت پاسخ‌گویی: {SUPPORT.hours}
      </Typography.Text>
    </Space>
  );
}

/**
 * The dialog behind «راهنما و پشتیبانی». It asks for the error's «کد پیگیری» —
 * and, since a toast fades long before anyone opens this, it keeps the last
 * one itself: a failure from the last half hour is offered here with a copy
 * button (bidi-safe, through the kit's `TrackingCode`). Nothing else about the
 * failure is kept: no message, no record ids.
 */
export function SupportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const last = open ? recentError() : null;
  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="راهنما و پشتیبانی بوکورا"
      footer={
        <Button type="primary" onClick={onClose}>
          بستن
        </Button>
      }
      destroyOnHidden
    >
      <Typography.Paragraph className="bk-muted">
        اگر در استفاده از پنل به مشکل خوردید یا متن پیامی روشن نبود، با ما تماس بگیرید. اگر پیام خطا
        «کد پیگیری» داره، اون رو هم بفرستید تا مشکل رو سریع‌تر بررسی کنیم.
      </Typography.Paragraph>
      {last ? (
        <Typography.Paragraph role="group" aria-label="آخرین خطا">
          <Typography.Text>آخرین خطایی که دیدید:</Typography.Text>{' '}
          <TrackingCode value={last.requestId} />
        </Typography.Paragraph>
      ) : null}
      <SupportContactList />
    </Modal>
  );
}

/**
 * A button that opens the support dialog — for error screens and messages
 * that would otherwise end at «با پشتیبانی تماس بگیرید».
 */
export function SupportButton({
  children = 'تماس با پشتیبانی',
  ...rest
}: Omit<ButtonProps, 'onClick'> & { children?: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button icon={<Icon name="chat" size={16} />} {...rest} onClick={() => setOpen(true)}>
        {children}
      </Button>
      <SupportDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
